import { IncorporationRequestDataType } from '@commenda/types';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import {
  CorporationType,
  LegacyDocumentType,
  WorkRequestType,
} from '@prisma/client';
import { Country, State } from 'country-state-city';
import { DocumentSummaryField, SummaryFieldKS } from '../common';
import { getDirectorName } from '../Director/common';
import { calculateCapTableData } from '../EquityInfo/common';

const USFounderField = ({
  director,
  data,
  workRequestType,
  shareholderDirectorIds,
}: {
  director: DirectorBase;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  workRequestType?: WorkRequestType;
  shareholderDirectorIds: Set<number>;
}) => {
  const openFile = async (fileId: number) => {
    const res = await getSignedUrlByFileId(fileId, null);
    window.open(res);
  };
  const officers = director?.corporationHasDirector
    ?.find(
      (dirWithCorp) =>
        dirWithCorp.directorId === director.id &&
        dirWithCorp.corporationId === data.corporationId
    )
    ?.additionalDirectorInformation?.boardPosition?.join(', ');

  const directorVsEquityMap: Record<
    number,
    { sharePercentage: number; name: string; id: number }
  > = {};

  let isAuthorizedSignatory = false;

  data?.directors?.forEach((dir) => {
    const details = dir.corporationHasDirector.find(
      (directorWithCorporation) =>
        directorWithCorporation.corporationId === data.corporationId &&
        (shareholderDirectorIds.has(dir.id) ||
          directorWithCorporation.additionalDirectorInformation?.isDirector)
    )?.additionalDirectorInformation;

    if (details) {
      isAuthorizedSignatory = details?.isAuthorizedSignatory;
      directorVsEquityMap[dir.id] = {
        name: getDirectorName(dir),
        sharePercentage: details.sharePercentage || 0,
        id: dir.id,
      };
    }
  });

  const {
    shareRetained,
    shareCount: totalShares,
    sharePrice: price,
  } = data.requestData;

  const capTableData = calculateCapTableData(
    totalShares,
    directorVsEquityMap
      ? Object.values(directorVsEquityMap).map((dir) => {
          return {
            name: dir.name,
            equityPercentage: dir.sharePercentage,
            id: dir.id,
          };
        })
      : null,
    shareRetained,
    price
  );

  const isCorporateStakeholdersLaunched = isFeatureLaunched(
    FeatureLaunch.US_INCORPORATION_CORPORATE_STAKEHOLDERS
  );

  const passportFiles = director?.directorFiles?.filter(
    (file) => file.documentType === LegacyDocumentType.Passport
  );
  const passportFile =
    passportFiles.length > 0 ? passportFiles[passportFiles.length - 1] : null;

  const governmentIdFile = director?.directorFiles?.find(
    (file) => file.documentType === LegacyDocumentType.GovernmentId
  );

  const directorInfo = capTableData?.find((dir) => dir.id === director.id);
  const physicalAddress = director?.operatingAddress;

  return (
    <div className="p-6 flex flex-col gap-2 bg-bg-disabled-subtle">
      <SummaryFieldKS
        heading="First Name"
        value={director?.directorFirstName || '-'}
      />
      <SummaryFieldKS
        heading="Last Name"
        value={director?.directorLastName || '-'}
      />

      <SummaryFieldKS heading="Email" value={director?.email} />
      <SummaryFieldKS heading="Phone Number" value={director?.number} />

      {director?.dateOfBirth ? (
        <SummaryFieldKS heading="Date of birth" value={director?.dateOfBirth} />
      ) : null}

      {director?.personalDetails?.nationality ? (
        <SummaryFieldKS
          heading="Nationality"
          value={
            Country.getCountryByCode(director?.personalDetails?.nationality)
              ?.name
          }
        />
      ) : null}

      {Country.getCountryByCode(director?.personalDetails?.residence)?.name && (
        <SummaryFieldKS
          heading="Country of residence"
          value={
            Country.getCountryByCode(director?.personalDetails?.residence)?.name
          }
        />
      )}

      {directorInfo?.ownership && !isCorporateStakeholdersLaunched ? (
        <SummaryFieldKS
          heading="Ownership"
          value={directorInfo?.ownership ? `${directorInfo.ownership}%` : null}
        />
      ) : null}
      {directorInfo?.esopPool && !isCorporateStakeholdersLaunched ? (
        <SummaryFieldKS
          heading="ESOP Pool Contribution"
          value={directorInfo?.esopPool ? `${directorInfo.esopPool}%` : null}
        />
      ) : null}
      {directorInfo?.numberOfShares &&
      data?.requestData?.corporationType === CorporationType.CCorp &&
      !isCorporateStakeholdersLaunched ? (
        <SummaryFieldKS
          heading="Shares"
          value={
            directorInfo?.numberOfShares
              ? new Intl.NumberFormat('en-GB').format(
                  directorInfo.numberOfShares
                )
              : null
          }
        />
      ) : null}
      {physicalAddress ? (
        <SummaryFieldKS
          heading="Current Address"
          value={[
            `${physicalAddress?.addressLine1}, ${physicalAddress?.addressLine2}`,
            `${physicalAddress?.city} - ${physicalAddress?.postalCode}`,
            `${
              State.getStateByCodeAndCountry(
                physicalAddress?.state,
                physicalAddress?.country
              )?.name
            }, ${Country.getCountryByCode(physicalAddress?.country)?.name}`,
          ]}
        />
      ) : null}
      {officers ? (
        <SummaryFieldKS heading="Roles" value={officers || 'No'} />
      ) : null}
      <SummaryFieldKS
        heading="Is authorized signatory"
        value={
          director.corporationHasDirector.find(
            (directorWithCorporation) =>
              directorWithCorporation.corporationId === data.corporationId &&
              (shareholderDirectorIds.has(director.id) ||
                directorWithCorporation.additionalDirectorInformation
                  ?.isDirector)
          )?.additionalDirectorInformation?.isAuthorizedSignatory
            ? 'Yes'
            : 'No'
        }
      />
      {data?.requestData?.corporationType === CorporationType.LLC && (
        <SummaryFieldKS
          heading="Is manager"
          value={
            director.corporationHasDirector.find(
              (directorWithCorporation) =>
                directorWithCorporation.corporationId === data.corporationId &&
                (shareholderDirectorIds.has(director.id) ||
                  directorWithCorporation.additionalDirectorInformation
                    ?.isDirector)
            )?.additionalDirectorInformation?.isManager
              ? 'Yes'
              : 'No'
          }
        />
      )}
      {passportFile?.fileUrl ? (
        <div className="mt-2">
          <DocumentSummaryField
            onClick={() => openFile(passportFile?.id)}
            title="Passport"
            className="grid grid-cols-2"
          />
        </div>
      ) : null}
      {governmentIdFile?.fileUrl ? (
        <div className="mt-2">
          <DocumentSummaryField
            onClick={() => openFile(governmentIdFile?.id)}
            title="Government ID"
            className="grid grid-cols-2"
          />
        </div>
      ) : null}
    </div>
  );
};

export default USFounderField;
